import {formatCurrencyValue} from "../../../util/PaymentUtils";
import {formatFrequency} from "@securspace/securspace-ui-kit";
import {formatEndDateDisplay} from "../../../util/DateUtils";
import {assetTypeOptions, frequencyOptions, BookingStatusGroups} from "../data";
import moment from "moment";
import {BookingStatus} from "../../constants/securspace-constants";
import {Booking} from "../../../types/Booking";

const sortByFields = [
  {label: "Booking Number", value: "ORDER_NUMBER"},
  {label: "Supplier", value: "SUPPLIER_COMPANY_NAME"},
  {label: "Start Date", value: "START_DATE"}
];

const filterBySelectOption = [
  {
    label: "Equipment Type",
    value: "",
    name: "equipmentType",
    option: assetTypeOptions
  },
  {
    label: "Frequency",
    value: "",
    name: "frequency",
    option: frequencyOptions
  }
];

const handleLocationClick = (booking?: Pick<Booking, "locationId">) => {
  if (booking?.locationId) {
    window.open(`/location-profile/${booking.locationId}`, '_blank');
  }
}

interface BuyerHeaderCell {
  id: string;
  label: string;
  onClick?: typeof handleLocationClick;
  cols?: number;
}

const buyerHeaderCells: BuyerHeaderCell[] = [
  {id: 'orderNumber', label: 'BOOKING NUMBER'},
  {id: 'frequencyValue', label: 'FREQUENCY'},
  {id: 'bookedDates', label: 'BOOKED DATES'},
  {id: 'locationName', label: 'LOCATION', onClick: handleLocationClick},
  {id: 'recurringCharge', label: 'MONTHLY CHARGE'},
  {id: 'status', label: 'STATUS'},
  {id: 'supplierCompanyName', label: 'SUPPLIER'},
  {id: 'numberOfSpaces', label: 'SPACES BOOKED'},
  {id: 'paymentSchedule', label: 'PAYMENT SCHEDULE'},
  {id: 'initialCharge', label: 'INITIAL CHARGE'},
  {id: 'paymentMethodDescription', label: 'PAYMENT METHOD'},
  {id: 'assetType', label: 'EQUIPMENT TYPE'},
  {id: 'creationDate', label: 'CREATED ON'},
  {id: 'rate', label: 'RATE PER SPACE'},
  {id: 'overageRate', label: 'OVERAGE RATE'},
  {id: 'combinedAddress', label: 'LOCATION ADDRESS'},
  {id: 'failedOveragesCount', label: 'PAST DUE OVERAGES'},
  {id: 'locationInstructions', label: 'LOCATION INSTRUCTIONS', cols: 4},
  {id: 'locationGateHours', label: 'GATE HOURS', cols: 4},
  {id: 'locationAfterHoursAccessProcedure', label: 'AFTER-HOURS ACCESS PROCEDURE', cols: 4},
];

const rateFrequencyLabel = Object.freeze({
  DAILY: "Day",
  WEEKLY: "Week",
  MONTHLY: "Month"
});

const isRateFrequency = (durationType?: string): durationType is keyof typeof rateFrequencyLabel =>
  !!durationType && durationType in rateFrequencyLabel;

function formatRateField(booking?: Booking) {
  if (booking && isRateFrequency(booking.durationType)) {
    let ratePerSpace = booking.rate;
    if (booking.brokered) {
      ratePerSpace = booking.brokeredBuyerChargedPerOccurrence / (booking.numberOfSpaces ? booking.numberOfSpaces : 1);
    }

    return `${formatCurrencyValue(ratePerSpace, true)}/${rateFrequencyLabel[booking.durationType]}`;
  }

  return '';
}

function formatOverageRateField(booking?: Booking) {
  if (booking?.chargeOverages) {
    if (booking.brokered && Number.isInteger(booking.brokeredBuyerOverageRateCharged)) {
      return formatCurrencyValue(booking.brokeredBuyerOverageRateCharged, true);
    } else {
      return formatCurrencyValue(booking.overageRate, true);
    }
  }

  return '';
}

// The display fields replace the raw numeric fields of the same name with formatted strings.
type DisplayFields = 'initialCharge' | 'recurringCharge' | 'rate' | 'overageRate';

export type TransformedBuyerBooking = Omit<Booking, DisplayFields> & {
  bookingTitle: string;
  initialCharge: string | number;
  recurringCharge: string | number;
  rate: string;
  overageRate: string | number;
  frequencyValue: string;
  bookedDates: string;
};

const transformBooking = (booking: Booking): TransformedBuyerBooking => {
  const commonProperties = {
    bookingTitle: booking.locationName ? `${booking.locationName} - ${booking.supplierCompanyName}` : booking.supplierCompanyName,
    initialCharge: formatCurrencyValue(booking.initialCharge, true),
    recurringCharge: formatCurrencyValue(booking.recurringCharge, true),
    rate: formatRateField(booking),
    overageRate: formatOverageRateField(booking),
    frequencyValue: formatFrequency(booking.frequency, booking.durationType),
    bookedDates: `${moment(booking.startDate).format("MM/DD/YYYY") || "N/A"} → ${formatEndDateDisplay(booking?.endDate, "MM/DD/YYYY")}`,
  };

  return {
    ...booking,
    ...commonProperties
  };
};

const BuyerBookingTabs = Object.freeze({
  ACTIVE: BookingStatusGroups.ACTIVE,
  PENDING: BookingStatusGroups.PENDING,
  INACTIVE: BookingStatusGroups.INACTIVE,
  FAILED: BookingStatusGroups.FAILED,
});

const showEndBookingStatuses = [
  BookingStatus.APPROVED,
  BookingStatus.APPROVED_ADJUSTED,
  BookingStatus.PAYMENT_DECLINED,
  BookingStatus.PAYMENT_DECLINED_ADJUSTED,
  BookingStatus.PROCESSING_ACH_PAYMENT,
  BookingStatus.MANUAL_PAYMENT
]


export {
  sortByFields,
  buyerHeaderCells,
  filterBySelectOption,
  transformBooking,
  BuyerBookingTabs,
  showEndBookingStatuses
}
